import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { useNavigate } from 'react-router-dom'

import { ProgramContent, WorkflowItem } from '@cibo/core'
import { ContactSupportButton } from '@cibo/profile'
import { useCommitFields, usePreflightCheck } from '@cibo/programs'
import { Markdown, ResponsiveDialog, WindowLocation, logRequestError } from '@cibo/ui'

import { LayoutSection } from 'components/ImpactLayout'
import { ROUTE } from 'navigation/types'
import { useAppStore } from 'store'

export const CommitProgram = () => {
  const {
    state: { programContent, workflow, fields, programId },
  } = useLocation() as WindowLocation<{
    programId: string
    fields: string[]
    programContent: { content: { fields: ProgramContent } }
    workflow: WorkflowItem
  }>

  const { fieldCounts } = workflow
  const { t } = useTranslation('impact/pages/CommitProgram')
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const { setEnrolledDuringSession } = useAppStore()

  const {
    mutateAsync: commitFields,
    isLoading: isCommitting,
    error: commitError,
    reset: resetQuery,
  } = useCommitFields({
    onSuccess: () => {
      setEnrolledDuringSession(true)
      setDialogOpen(true)
    },
  })

  const {
    mutateAsync: preflightCheck,
    isLoading: loadingPreflightCheck,
    error: preflightError,
    data: preflightData,
  } = usePreflightCheck()

  useEffect(() => {
    preflightCheck({ programId, resourceIds: fields })
  }, [])

  const onAgree = () => {
    commitFields({ programId, resourceIds: fields, deleteWfOnEmpty: true }).catch(error => {
      logRequestError(error, { preflightData })
    })
  }

  return (
    <>
      <LayoutSection container autoHeight>
        <Divider />
        <Stack spacing={2}>
          <Typography variant="h6">
            {t('commitTitle', {
              programName: programContent?.content.fields.title,
              count: fields.length,
            })}
          </Typography>
        </Stack>
        <Box>
          <Grid container spacing={4}>
            <Grid item md={12} lg={6}>
              <Markdown>
                {programContent?.content.fields.commitDisclosure || t('defaultMessage')}
              </Markdown>
            </Grid>
            <Grid xs={12} item>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(ROUTE.PROGRAMS)}
                  disabled={fieldCounts.total === 0}
                >
                  {t('cancel')}
                </Button>
                <LoadingButton
                  data-testid="commit-agree-button"
                  loading={isCommitting || loadingPreflightCheck}
                  variant="contained"
                  disabled={
                    fieldCounts.total === 0 ||
                    (!!fieldCounts.total && !!fieldCounts.incomplete) ||
                    !!preflightError
                  }
                  onClick={onAgree}
                >
                  {t('agree')}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </LayoutSection>

      <ResponsiveDialog maxWidth="xs" open={!!commitError} onClose={() => resetQuery()}>
        <DialogTitle>{t('commitErrorTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Markdown>{t('commitError', { commitError })}</Markdown>

            <ContactSupportButton />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => resetQuery()}>
            {t('close')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog
        maxWidth="xs"
        open={dialogOpen}
        data-testid="commit-congratulations"
        onClose={() => setDialogOpen(false)}
      >
        <DialogTitle>{t('congratulationsTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText variant="body1" color="text.primary">
              <Markdown>
                {programContent?.content.fields.commitConfirmationSuccessMessage ||
                  t('congratulationsSummary', {
                    count: fields.length,
                    programName: programContent?.content.fields.title,
                  })}
              </Markdown>
            </DialogContentText>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={() => navigate(ROUTE.PROGRAMS)}>
            {t('finish')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </>
  )
}
